import { useCallback, useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useFormikContext } from 'formik';
import { useQueryClient } from '@tanstack/react-query';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';

import {
  getDocumentName,
  getFragmentData,
  ThirdPartyTypeEnum,
} from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';

import { generateId } from '../../../../utils/generate-id.util';
import { Loading } from '../../../../components/Loading';
import { ActorCardCmp_Organization_Fragment } from '../../../../components/Actors/Card/fragments/organization.fragment';
import { OrganizationPage_OneOrganization_QueryDocument } from '../../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { ThirdPartiesSelectionFormStep } from '../../../ThirdParty/steps/Selection';
import type {
  UniqueIdMaybeEmptyFieldValue,
  UniqueUsageMaybeEmptyFieldValue,
} from '../../../ThirdParty/steps/Selection/types/field-value.type';

import type { Representative } from '../../fragments/representative.fragment';
import type { ActorForm_Actor } from '../../fragments/actor.fragment';
import { useRepresentationReferences } from '../../hooks/useRepresentationReferences.hook';
import { newRepresentativeOf } from '../../utils/new-representative.util';
import { representativeRequirementOf } from '../../utils/representation-requirements.util';
import type { RepresentationRules } from '../../utils/representation-requirements.util';

import { useInitialGridState } from '../ThirdPartySelect/hooks/useInitialGridState.hook';

interface Props {
  readonly id: string;
  readonly isInternal: boolean;
  readonly organization:
    FragmentType<typeof ActorCardCmp_Organization_Fragment> | undefined;
  readonly representationRules: RepresentationRules;
  readonly setHasRepresentatives: Dispatch<SetStateAction<boolean>>;
}

const getId = (representative: UniqueUsageMaybeEmptyFieldValue) =>
  !representative ? EMPTY_STRING : (representative as Representative).personId;

const enrichValue = (id: UniqueIdMaybeEmptyFieldValue, index: number) =>
  !id ? null : newRepresentativeOf(id, index);

export const ActorRepresentativesSelectFormStep = ({
  isInternal,
  organization: pOrganization,
  representationRules,
  setHasRepresentatives,
}: Readonly<Props>) => {
  const queryClient = useQueryClient();
  const { values } = useFormikContext<ActorForm_Actor>();

  // Without representative, the representatives details step has nothing to detail.
  const hasRepresentatives =
    values.__typename === 'ActorOrganization' &&
    !!values.representatives.length;
  useEffect(() => {
    setHasRepresentatives(hasRepresentatives);
  }, [hasRepresentatives, setHasRepresentatives]);
  const { genericStatusesCodeMap } = useReferencesContext();
  const representationReferences = useRepresentationReferences();

  const { initialGridState } = useInitialGridState();

  const organization = getFragmentData(
    ActorCardCmp_Organization_Fragment,
    pOrganization,
  );

  const handleCreateSuccess = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [
        getDocumentName(OrganizationPage_OneOrganization_QueryDocument),
        { id: organization?._id },
      ],
    });
  }, [organization?._id, queryClient]);

  const organizationId = organization?._id;

  const initialPersonValues = useMemo(
    () =>
      organizationId
        ? {
            activeMemberships: [
              {
                __typename: 'PersonMembership' as const,
                _id: generateId(),
                contextualRoleIds: null,
                functions: [],
                organizationId,
                representativeTypeIds: null,
              },
            ],
          }
        : undefined,
    [organizationId],
  );

  const memberIds = useMemo(() => {
    const enabledStatusId = genericStatusesCodeMap.Enabled._id;

    const activeMemberIds = (organization?.allMemberships ?? [])
      .filter((membership) => membership.statusId === enabledStatusId)
      .map((membership) => membership.personId);

    const currentRepresentativeIds =
      values.__typename === 'ActorOrganization'
        ? values.representatives.map(
            (representative) => representative.personId,
          )
        : [];

    return [...new Set([...activeMemberIds, ...currentRepresentativeIds])];
  }, [
    genericStatusesCodeMap.Enabled._id,
    organization?.allMemberships,
    values,
  ]);

  if (!organization) {
    return <Loading />;
  }

  const isUnrepresented =
    values.__typename === 'ActorOrganization' && !values.representatives.length;
  const requirement = representativeRequirementOf(
    { isInternal, categoryIds: values.categoryIds },
    representationRules,
    representationReferences,
  );

  // The alerts keep their height, the list takes the rest of the step.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!(isUnrepresented && requirement) && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {requirement}
        </Alert>
      )}
      {/* An external organization may have no representative: the user is told
      what it means for the documents. */}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!(isUnrepresented && !requirement) && (
        <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
          <AlertTitle>Aucun représentant sélectionné</AlertTitle>
          Cette organisation n&apos;aura pas d&apos;interlocuteur.
          <br />
          Un devis, mail ou courrier qui lui sera adressé ne nommera personne («
          Madame, Monsieur »).
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        <ThirdPartiesSelectionFormStep
          creationPrefill={{
            forPerson: initialPersonValues,
          }}
          enrichValue={enrichValue}
          fieldName="representatives"
          getId={getId}
          id="representatives"
          initialGridState={initialGridState}
          isInternal={isInternal}
          kind={ThirdPartyTypeEnum.Person}
          onCreateSuccess={handleCreateSuccess}
          subsetIds={memberIds}
        />
      </Box>
    </Box>
  );
};
